import { useEffect, useRef, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

interface SkuSuggestion {
  code: string;
  nameLt: string;
}

type SuggestionField = 'code' | 'nameLt';

/**
 * SKU code/name input with a live, read-only list of occupied prefixes
 * (WMS-50, WMS-85). Code suggestions deliberately are not selectable because
 * choosing an occupied code would only make the create request fail. Both
 * fields show a faint inline completion so the user sees the full existing
 * value without replacing what they typed.
 */
function SkuSuggestionInput({
  partnerId,
  value,
  onChange,
  field,
  required,
  className,
}: {
  partnerId: string;
  value: string;
  onChange: (value: string) => void;
  field: SuggestionField;
  required?: boolean;
  className?: string;
}) {
  const [suggestions, setSuggestions] = useState<SkuSuggestion[]>([]);
  const requestId = useRef(0);
  const prefix = value.trim();

  useEffect(() => {
    const currentRequest = ++requestId.current;
    if (!prefix) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      api<SkuSuggestion[]>(
        `/partners/${partnerId}/skus/code-suggestions?prefix=${encodeURIComponent(prefix)}&field=${field}`,
      )
        .then((rows) => {
          if (requestId.current === currentRequest) setSuggestions(rows);
        })
        .catch(() => {
          if (requestId.current === currentRequest) setSuggestions([]);
        });
    }, 150);
    return () => clearTimeout(timer);
  }, [field, partnerId, prefix]);

  const firstValue = suggestions[0]?.[field] ?? '';
  const completionSuffix =
    value === prefix &&
    firstValue.length > value.length &&
    firstValue.toLocaleLowerCase().startsWith(value.toLocaleLowerCase())
      ? firstValue.slice(value.length)
      : '';

  return (
    <div className="space-y-1">
      <div className="relative">
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required={required}
          maxLength={field === 'code' ? 64 : 200}
          autoComplete="off"
          className={className}
        />
        {completionSuffix ? (
          <div
            aria-hidden="true"
            className={`pointer-events-none absolute inset-0 flex items-center overflow-hidden px-2 py-1.5 ${field === 'code' ? 'font-mono' : ''}`}
          >
            <span className="invisible whitespace-pre">{value}</span>
            <span className="whitespace-pre text-muted-foreground/50">{completionSuffix}</span>
          </div>
        ) : null}
      </div>
      {suggestions.length > 0 ? (
        <div className="rounded border border-border bg-muted/40 px-2 py-1.5 text-xs">
          <div className="mb-1 text-muted-foreground">
            {t(field === 'code' ? 'sku.code_suggestions' : 'sku.name_suggestions')}
          </div>
          <ul className="max-h-40 space-y-0.5 overflow-y-auto" aria-live="polite">
            {suggestions.map((suggestion) => (
              <li key={suggestion.code}>
                <span className="font-mono font-medium">{suggestion.code}</span>
                <span className="text-muted-foreground"> — {suggestion.nameLt}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function SkuCodeInput(props: Omit<Parameters<typeof SkuSuggestionInput>[0], 'field'>) {
  return <SkuSuggestionInput {...props} field="code" />;
}

export function SkuNameInput(props: Omit<Parameters<typeof SkuSuggestionInput>[0], 'field'>) {
  return <SkuSuggestionInput {...props} field="nameLt" />;
}
